import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Eye,
  AlertTriangle,
  Database,
  FolderClosed,
  KeyRound,
  Receipt,
  Search,
  Users,
} from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Badge, StatusBadge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { useIsPlatform } from '@/store/auth';
import { Input, Select } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { EmptyState, PageLoader, TableSkeleton } from '@/components/ui/States';
import { Pagination, Table, TableWrapper, Td, Th, Tr } from '@/components/ui/Table';
import {
  formatBytes,
  formatDate,
  formatDateTime,
  formatDuration,
  formatIdr,
  formatNumber,
} from '@/lib/format';
import { cn } from '@/lib/cn';

/**
 * Data pelanggan untuk admin.
 *
 * Pertanyaan dukungan hampir selalu berbentuk "kenapa punya saya begini".
 * Sebelum ada layar ini, menjawabnya berarti membuka mongosh di server dan
 * mengetik agregasi, jadi tiap pertanyaan kecil menuntut akses SSH beserta
 * risiko salah ketik pada basis data yang sedang dipakai.
 *
 * Yang ditampilkan METADATA, bukan isi datanya. Anda bisa melihat sebuah job
 * mengambil 4.120 baris dari sebuah domain, tetapi tidak bisa membaca
 * baris-barisnya. Data hasil scraping milik pelanggan, dan membacanya tidak
 * diperlukan untuk menjawab satu pun pertanyaan dukungan yang wajar.
 */

const PAKET = [
  ['', 'Semua paket'],
  ['free', 'Free'],
  ['starter', 'Starter'],
  ['growth', 'Growth'],
  ['pro', 'Pro'],
  ['business', 'Business'],
];

const NADA_PAKET = {
  free: 'neutral',
  starter: 'info',
  growth: 'accent',
  pro: 'brand',
  business: 'success',
};

/** Satu angka besar dengan keterangan kecil di bawahnya. */
const Angka = ({ label, nilai, catatan, nada }) => (
  <div className="rounded-lg border border-[var(--border-subtle)] p-3">
    <p className="text-xs text-[var(--text-muted)]">{label}</p>
    <p className={cn('mt-0.5 text-lg font-bold', nada ?? 'text-[var(--text-primary)]')}>{nilai}</p>
    {catatan && <p className="text-[11px] text-[var(--text-muted)]">{catatan}</p>}
  </div>
);

/** Bilah pemakaian versus batas. */
const Bilah = ({ label, dipakai, batas, format = formatNumber }) => {
  const persen = batas > 0 ? Math.min(100, Math.round((dipakai / batas) * 100)) : 0;
  return (
    <div>
      <div className="flex items-baseline justify-between gap-2 text-xs">
        <span className="font-semibold text-[var(--text-primary)]">{label}</span>
        <span className="text-[var(--text-muted)]">
          {format(dipakai)} / {format(batas)}
        </span>
      </div>
      <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-[var(--surface-sunken)]">
        <div
          className={cn(
            'h-full rounded-full',
            persen >= 90 ? 'bg-danger-500' : persen >= 70 ? 'bg-warning-500' : 'bg-accent-500',
          )}
          style={{ width: `${persen}%` }}
        />
      </div>
    </div>
  );
};

const Bagian = ({ icon: Icon, judul, jumlah, kosong, children }) => (
  <div>
    <p className="mb-2 flex items-center gap-2 text-sm font-bold text-[var(--text-primary)]">
      <Icon className="size-4 text-[var(--text-secondary)]" aria-hidden="true" />
      {judul}
      {jumlah !== undefined && (
        <span className="text-xs font-normal text-[var(--text-muted)]">({formatNumber(jumlah)})</span>
      )}
    </p>
    {children ?? <p className="text-xs text-[var(--text-muted)]">{kosong}</p>}
  </div>
);

/** Tabel ringkas untuk isi dialog. Sengaja tanpa paginasi: yang ditarik
 *  hanya 20 baris terakhir, dan itu yang relevan untuk dukungan. */
const TabelKecil = ({ kolom, baris, kunci, render }) => (
  <div className="overflow-x-auto rounded-lg border border-[var(--border-subtle)]">
    <table className="w-full text-xs">
      <thead className="bg-[var(--surface-sunken)]">
        <tr>
          {kolom.map((k) => (
            <th key={k} className="px-3 py-2 text-left font-semibold text-[var(--text-secondary)]">
              {k}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-[var(--border-subtle)]">
        {baris.map((b) => (
          <tr key={kunci(b)}>{render(b)}</tr>
        ))}
      </tbody>
    </table>
  </div>
);

const Sel = ({ children, className }) => (
  <td className={cn('px-3 py-2 text-[var(--text-secondary)]', className)}>{children}</td>
);

// -- Isi dialog -----------------------------------------------------------

/**
 * Isi data sebuah job milik pelanggan.
 *
 * ── Kenapa dibatasi seratus baris ───────────────────────────────────────
 *
 * Yang dibutuhkan untuk memahami sebuah keluhan adalah melihat BENTUK
 * datanya: kolomnya apa, nilainya masuk akal atau tidak, ada yang kosong atau
 * tidak. Menyalin seluruh isinya bukan bagian dari menjawab pertanyaan
 * dukungan, dan batas ini membuat perbedaan itu tegas.
 */
const DialogIsiJob = ({ orgId, job, onClose }) => {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['isi-job-pelanggan', orgId, job.id],
    queryFn: () => api.get(`/platform/organizations/${orgId}/jobs/${job.id}/data`),
  });

  const isi = data?.data;

  return (
    <Modal open onClose={onClose} size="lg" title={job.nama} description={job.domain ?? job.url}>
      <div className="space-y-4">
        <p className="rounded-lg border border-warning-200 bg-warning-50 px-3 py-2 text-xs leading-relaxed text-warning-700 dark:border-warning-500/25 dark:bg-warning-500/10 dark:text-warning-500">
          Ini data milik pelanggan. Pembacaannya tercatat di Jejak Audit beserta nama Anda.
        </p>

        {isLoading ? (
          <p className="py-6 text-center text-sm text-[var(--text-muted)]">Memuat isi data...</p>
        ) : isError ? (
          <p className="py-6 text-center text-sm text-danger-500">
            {error instanceof ApiError ? error.message : 'Isi data tidak bisa dibaca.'}
          </p>
        ) : isi?.baris?.length === 0 ? (
          <p className="py-6 text-center text-sm text-[var(--text-muted)]">
            Job ini belum punya baris data tersimpan.
          </p>
        ) : (
          <>
            <p className="text-xs text-[var(--text-muted)]">
              Menampilkan {isi.ditampilkan} dari {formatNumber(isi.job.totalBaris)} baris.
            </p>
            <div className="max-h-96 overflow-auto rounded-lg border border-[var(--border-subtle)]">
              <table className="w-full border-collapse text-xs">
                <thead className="sticky top-0 bg-[var(--surface-sunken)]">
                  <tr>
                    {isi.kolom.map((k) => (
                      <th
                        key={k}
                        className="border-b border-[var(--border-subtle)] px-2.5 py-1.5 text-left font-bold whitespace-nowrap text-[var(--text-secondary)]"
                      >
                        {k}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {isi.baris.map((b, i) => (
                    <tr key={i}>
                      {isi.kolom.map((k) => (
                        <td
                          key={k}
                          className="max-w-[16rem] truncate border-b border-[var(--border-subtle)] px-2.5 py-1.5 text-[var(--text-muted)]"
                          title={String(b.data?.[k] ?? '')}
                        >
                          {String(b.data?.[k] ?? '')}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

const DetailAkun = ({ organizationId }) => {
  // Hanya root yang boleh membaca isi data pelanggan. Server tetap menolak
  // yang lain; ini supaya tombol yang pasti gagal tidak muncul.
  const isRoot = useIsPlatform('root');
  const [intip, setIntip] = useState(null);
  const { data, isLoading, error } = useQuery({
    queryKey: ['platform', 'organizations', organizationId],
    queryFn: () => api.get(`/platform/organizations/${organizationId}`),
    enabled: Boolean(organizationId),
  });

  if (isLoading) return <PageLoader label="Membuka isi akun..." />;
  if (error || !data?.data) {
    return <p className="text-sm text-[var(--text-secondary)]">Isi akun tidak bisa dimuat.</p>;
  }

  const d = data.data;
  const u = d.pemakaian;

  return (
    <div className="space-y-6">
      <div className="grid gap-3 sm:grid-cols-3">
        <Angka
          label="Paket"
          nilai={u.plan?.name ?? d.organisasi.paket.id}
          catatan={
            d.organisasi.paket.berakhir
              ? `Berlaku sampai ${formatDate(d.organisasi.paket.berakhir)}`
              : 'Tanpa tanggal berakhir'
          }
        />
        <Angka
          label="Job"
          nilai={formatNumber(d.ringkasan.totalJob)}
          catatan={`${formatNumber(d.ringkasan.totalRun)} kali dijalankan`}
        />
        <Angka
          label="Eksekusi gagal"
          nilai={`${d.ringkasan.persenGagal}%`}
          catatan={`${formatNumber(d.ringkasan.runGagal)} dari ${formatNumber(d.ringkasan.totalRun)}`}
          nada={d.ringkasan.persenGagal >= 20 ? 'text-danger-600' : undefined}
        />
      </div>

      <div className="space-y-3 rounded-lg border border-[var(--border-subtle)] p-4">
        <p className="text-sm font-bold text-[var(--text-primary)]">
          Pemakaian periode berjalan
          <span className="ml-2 text-xs font-normal text-[var(--text-muted)]">
            {formatDate(u.periodStart)} sampai {u.periodEnd ? formatDate(u.periodEnd) : 'tanpa batas'}
          </span>
        </p>
        <Bilah label="Baris data" dipakai={u.records.used} batas={u.records.limit} />
        <Bilah
          label="Penyimpanan"
          dipakai={u.storage.used}
          batas={u.storage.limit}
          format={formatBytes}
        />
        <Bilah label="Job tersimpan" dipakai={u.jobs.used} batas={u.jobs.limit} />
      </div>

      <Bagian icon={Users} judul="Anggota" jumlah={d.anggota.length} kosong="Tidak ada anggota aktif.">
        {d.anggota.length > 0 && (
          <TabelKecil
            kolom={['Nama', 'Email', 'Peran', 'Verifikasi', 'Masuk terakhir']}
            baris={d.anggota}
            kunci={(a) => a.id}
            render={(a) => (
              <>
                <Sel className="font-semibold text-[var(--text-primary)]">{a.nama}</Sel>
                <Sel>{a.email}</Sel>
                <Sel>
                  <Badge size="sm" tone="neutral">
                    {a.peranOrganisasi}
                  </Badge>
                </Sel>
                <Sel>
                  {a.emailTerverifikasi ? (
                    <Badge size="sm" tone="success">
                      Terverifikasi
                    </Badge>
                  ) : (
                    <Badge size="sm" tone="warning">
                      Belum
                    </Badge>
                  )}
                </Sel>
                <Sel>{a.masukTerakhir ? formatDateTime(a.masukTerakhir) : 'Belum pernah'}</Sel>
              </>
            )}
          />
        )}
      </Bagian>

      <Bagian
        icon={Database}
        judul="Job"
        jumlah={d.ringkasan.totalJob}
        kosong="Belum ada job di akun ini."
      >
        {d.job.length > 0 && (
          <TabelKecil
            kolom={[
              'Nama',
              'Sumber',
              'Status',
              'Jadwal',
              'Baris',
              'Terakhir jalan',
              ...(isRoot ? [''] : []),
            ]}
            baris={d.job}
            kunci={(j) => j.id}
            render={(j) => (
              <>
                <Sel className="font-semibold text-[var(--text-primary)]">{j.nama}</Sel>
                <Sel>{j.domain ?? j.url}</Sel>
                <Sel>
                  <StatusBadge status={j.status} size="sm" />
                </Sel>
                <Sel>{j.berjadwal ? j.frekuensi : 'Manual'}</Sel>
                <Sel>{formatNumber(j.totalBaris)}</Sel>
                <Sel>{j.terakhirJalan ? formatDateTime(j.terakhirJalan) : 'Belum pernah'}</Sel>
                {/* Hanya root. Membaca data pelanggan adalah pengecualian dari
                    aturan yang dibangun sengaja, dan setiap pembacaannya
                    tercatat di jejak audit. */}
                {isRoot && (
                  <Sel>
                    <Button
                      size="xs"
                      variant="ghost"
                      icon={Eye}
                      onClick={() => setIntip({ orgId: organizationId, job: j })}
                    >
                      Lihat isi
                    </Button>
                  </Sel>
                )}
              </>
            )}
          />
        )}
      </Bagian>

      <Bagian
        icon={AlertTriangle}
        judul="Eksekusi terakhir"
        kosong="Belum ada eksekusi tercatat."
      >
        {d.riwayat.length > 0 && (
          <TabelKecil
            kolom={['Job', 'Status', 'Pemicu', 'Mulai', 'Durasi', 'Baris', 'Galat']}
            baris={d.riwayat}
            kunci={(r) => r.id}
            render={(r) => (
              <>
                <Sel className="font-semibold text-[var(--text-primary)]">{r.namaJob}</Sel>
                <Sel>
                  <StatusBadge status={r.status} size="sm" />
                </Sel>
                <Sel>{r.pemicu}</Sel>
                <Sel>{r.mulai ? formatDateTime(r.mulai) : '-'}</Sel>
                <Sel>{r.durasiMs ? formatDuration(r.durasiMs) : '-'}</Sel>
                <Sel>{formatNumber(r.baris)}</Sel>
                {/* Warna merah hanya kalau memang ada galatnya. Tanda hubung
                    merah pada eksekusi yang berhasil membuat seluruh kolom
                    terlihat bermasalah padahal tidak ada yang gagal. */}
                <Sel
                  className={cn('max-w-[220px] truncate', r.galat && 'text-danger-600')}
                  title={r.galat ?? ''}
                >
                  {r.galat ?? '-'}
                </Sel>
              </>
            )}
          />
        )}
      </Bagian>

      <Bagian
        icon={FolderClosed}
        judul="Berkas"
        jumlah={d.ringkasan.totalBerkas}
        kosong="Belum ada berkas tersimpan."
      >
        {d.berkas.length > 0 && (
          <TabelKecil
            kolom={['Nama', 'Format', 'Ukuran', 'Baris', 'Sumber', 'Diunduh', 'Dibuat']}
            baris={d.berkas}
            kunci={(f) => f.id}
            render={(f) => (
              <>
                <Sel className="font-semibold text-[var(--text-primary)]">{f.nama}</Sel>
                <Sel className="uppercase">{f.format}</Sel>
                <Sel>{formatBytes(f.ukuran)}</Sel>
                <Sel>{f.baris === null ? '-' : formatNumber(f.baris)}</Sel>
                <Sel>{f.sumber}</Sel>
                <Sel>{formatNumber(f.diunduh)}</Sel>
                <Sel>{formatDate(f.dibuat)}</Sel>
              </>
            )}
          />
        )}
      </Bagian>

      <Bagian
        icon={KeyRound}
        judul="Kunci API aktif"
        jumlah={d.kunciApi.length}
        kosong="Akun ini belum membuat kunci API."
      >
        {d.kunciApi.length > 0 && (
          <>
            <TabelKecil
              kolom={['Nama', 'Awalan', 'Scope', 'Dipakai terakhir']}
              baris={d.kunciApi}
              kunci={(k) => k.id}
              render={(k) => (
                <>
                  <Sel className="font-semibold text-[var(--text-primary)]">{k.nama}</Sel>
                  <Sel className="font-mono">{k.awalan}...</Sel>
                  <Sel>{k.scopes.join(', ') || '-'}</Sel>
                  <Sel>{k.dipakaiTerakhir ? formatDateTime(k.dipakaiTerakhir) : 'Belum pernah'}</Sel>
                </>
              )}
            />
            <p className="mt-1.5 text-[11px] text-[var(--text-muted)]">
              Hanya awalannya. Kunci utuh tidak bisa dibaca siapa pun sesudah diterbitkan, termasuk
              oleh Anda, dan itu memang disengaja.
            </p>
          </>
        )}
      </Bagian>

      {d.addOn.length > 0 && (
        <Bagian icon={Database} judul="Add-on aktif" jumlah={d.addOn.length}>
          <TabelKecil
            kolom={['Nama', 'Jenis', 'Jumlah', 'Berakhir']}
            baris={d.addOn}
            kunci={(a) => a.id}
            render={(a) => (
              <>
                <Sel className="font-semibold text-[var(--text-primary)]">{a.nama}</Sel>
                <Sel>{a.jenis}</Sel>
                <Sel>
                  {a.jenis === 'storage' ? formatBytes(a.total) : formatNumber(a.total)}
                  <span className="text-[var(--text-muted)]"> ({a.jumlah}x)</span>
                </Sel>
                <Sel>{formatDate(a.berakhir)}</Sel>
              </>
            )}
          />
        </Bagian>
      )}

      <Bagian
        icon={Receipt}
        judul="Transaksi terakhir"
        kosong="Belum ada transaksi."
      >
        {d.transaksi.length > 0 && (
          <TabelKecil
            kolom={['Order', 'Isi', 'Nominal', 'Status', 'Dibuat']}
            baris={d.transaksi}
            kunci={(t) => t.id}
            render={(t) => (
              <>
                <Sel className="font-mono text-[var(--text-primary)]">{t.orderId}</Sel>
                <Sel>{t.isi.join(', ')}</Sel>
                <Sel>{formatIdr(t.nominal)}</Sel>
                <Sel>
                  <Badge
                    size="sm"
                    tone={
                      t.status === 'paid'
                        ? 'success'
                        : t.status === 'pending'
                          ? 'warning'
                          : t.status === 'refunded'
                            ? 'info'
                            : 'neutral'
                    }
                  >
                    {t.status}
                  </Badge>
                </Sel>
                <Sel>{formatDateTime(t.dibuat)}</Sel>
              </>
            )}
          />
        )}
      </Bagian>

      {intip && (
        <DialogIsiJob
          orgId={intip.orgId}
          job={intip.job}
          onClose={() => setIntip(null)}
        />
      )}
    </div>
  );
};

// -- Halaman --------------------------------------------------------------

const CustomersAdminPage = () => {
  const [search, setSearch] = useState('');
  const [paket, setPaket] = useState('');
  const [page, setPage] = useState(1);
  const [dibuka, setDibuka] = useState(null);

  const params = new URLSearchParams({ page: String(page), limit: '20' });
  if (search.trim()) params.set('search', search.trim());
  if (paket) params.set('plan', paket);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['platform', 'organizations', params.toString()],
    queryFn: () => api.get(`/platform/organizations?${params.toString()}`),
    placeholderData: (sebelumnya) => sebelumnya,
  });

  const items = data?.data ?? [];
  const meta = data?.meta ?? {};

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-bold text-[var(--text-primary)]">Data Pelanggan</h1>
        <p className="mt-1 max-w-3xl text-sm text-[var(--text-secondary)]">
          Isi tiap akun: job, berkas, riwayat eksekusi, kuota, dan transaksinya. Yang tampil di sini
          keterangan tentang datanya, bukan datanya sendiri, jadi hasil scraping pelanggan tetap
          hanya bisa dibaca pelanggan.
        </p>
      </div>

      <Card>
        <CardHeader
          title="Semua akun"
          description={
            meta.total !== undefined ? `${formatNumber(meta.total)} akun terdaftar` : undefined
          }
        />
        <CardBody className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <div className="relative min-w-[220px] flex-1">
              <Search
                className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-[var(--text-muted)]"
                aria-hidden="true"
              />
              <Input
                placeholder="Cari nama akun"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="pl-9"
              />
            </div>
            <Select
              value={paket}
              onChange={(e) => {
                setPaket(e.target.value);
                setPage(1);
              }}
              className="max-w-[180px]"
            >
              {PAKET.map(([nilai, label]) => (
                <option key={nilai} value={nilai}>
                  {label}
                </option>
              ))}
            </Select>
          </div>

          {isLoading ? (
            <TableSkeleton rows={6} columns={6} />
          ) : items.length === 0 ? (
            <EmptyState
              icon={Users}
              title="Tidak ada yang cocok"
              description="Coba kata kunci lain atau lepaskan saringan paketnya."
            />
          ) : (
            <>
              <TableWrapper>
                <Table>
                  <thead>
                    <tr>
                      <Th>Akun</Th>
                      <Th>Pemilik</Th>
                      <Th>Paket</Th>
                      <Th align="right">Job</Th>
                      <Th align="right">Baris</Th>
                      <Th align="right">Penyimpanan</Th>
                      <Th>Terakhir berubah</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((o) => (
                      <Tr key={o.id} onClick={() => setDibuka(o)}>
                        <Td>
                          <p className="font-semibold text-[var(--text-primary)]">{o.nama}</p>
                          <p className="text-xs text-[var(--text-muted)]">
                            {formatNumber(o.anggota)} anggota
                          </p>
                        </Td>
                        <Td>
                          {o.pemilik ? (
                            <>
                              <p className="text-[var(--text-primary)]">{o.pemilik.nama}</p>
                              <p className="text-xs text-[var(--text-muted)]">{o.pemilik.email}</p>
                            </>
                          ) : (
                            <span className="text-[var(--text-muted)]">Pemilik terhapus</span>
                          )}
                        </Td>
                        <Td>
                          <Badge size="sm" tone={NADA_PAKET[o.paket] ?? 'neutral'}>
                            {o.paket}
                          </Badge>
                        </Td>
                        <Td align="right">{formatNumber(o.job)}</Td>
                        <Td align="right">{formatNumber(o.baris)}</Td>
                        <Td align="right">{formatBytes(o.penyimpanan)}</Td>
                        <Td>{formatDate(o.terakhirBerubah)}</Td>
                      </Tr>
                    ))}
                  </tbody>
                </Table>
              </TableWrapper>

              <Pagination
                page={meta.page ?? page}
                totalPages={meta.totalPages ?? 1}
                total={meta.total ?? items.length}
                limit={meta.limit ?? 20}
                onPageChange={setPage}
                className={isFetching ? 'opacity-60' : undefined}
              />
            </>
          )}
        </CardBody>
      </Card>

      <Modal
        open={Boolean(dibuka)}
        onClose={() => setDibuka(null)}
        size="xl"
        title={dibuka?.nama}
        description={dibuka?.pemilik ? `Pemilik: ${dibuka.pemilik.email}` : undefined}
      >
        {dibuka && <DetailAkun organizationId={dibuka.id} />}
      </Modal>
    </div>
  );
};

export default CustomersAdminPage;
